import { Done, DoneAll } from "@mui/icons-material";
import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import InitialsAvatar from "../../../common/components/functional/initialsAvatar/InitialsAvatar";
import { ChatMessage } from "../types/queries";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { isReadByRecipient } from "../readReceipt";
import { selectThreadParticipants } from "../messagingSlice";
import { MessageAttachment } from "../attachments/types";
import { openConversationAttachment } from "../messagingActions";
import { format, parseISO } from "date-fns";
import { dateTimeFormatLong } from "../../../common/helpers/formatDateTime";
import { NameIdOption } from "../../../common/types/commonTypes";
import LinkifyTypography from "../../../common/components/functional/typography/LinkifyTypography";
import MessageAttachmentChip from "./MessageAttachmentChip";

type Props = {
  participant: NameIdOption;
  message: ChatMessage;
  compact?: boolean;
};

const Message = ({ message, participant, compact = false }: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("messaging");
  const { user } = useAppSelector((state) => state.user);
  const participants = useAppSelector((state) =>
    selectThreadParticipants(state, message.conversationId),
  );
  const theme = useTheme();

  const isMyMessage = message.senderId === user?.id;
  const showReceipt = isMyMessage && !message.pending;
  const readByRecipient =
    showReceipt &&
    isReadByRecipient(message.readBy, participants, user?.id ?? "");

  const downloadAttachment = (attachment: MessageAttachment) => {
    if (
      attachment.status !== "ready" ||
      !attachment.pathspec ||
      !message.conversationId
    ) {
      return;
    }
    void openConversationAttachment(dispatch, {
      conversationId: message.conversationId,
      messageId: message.id,
      pathspec: attachment.pathspec,
    });
  };

  return (
    <Box
      display="flex"
      gap="16px"
      padding="8px 0px"
      justifyContent={isMyMessage ? "end" : "start"}
    >
      {!isMyMessage && <InitialsAvatar name={participant?.name} width="32px" />}
      <Box
        sx={{
          maxWidth: "698px",
          minWidth: 0,
          width: compact ? "70%" : "80%",
          bgcolor: isMyMessage ? theme.palette.primary.light : "#F3F3F7",
          padding: "8px 12px",
          borderTopLeftRadius: isMyMessage ? "10px" : "0",
          borderBottomRightRadius: "10px",
          borderBottomLeftRadius: "10px",
          borderTopRightRadius: "10px",
          color: isMyMessage ? "#fff" : theme.palette.text.primary,
          marginRight: "16px",
        }}
      >
        {!!message.text && (
          <LinkifyTypography
            text={message.text}
            enlargeEmojis
            sx={{
              whiteSpace: "pre-wrap",
              overflowWrap: "break-word",
              wordBreak: "break-word",
              hyphens: "auto",
            }}
            LinkProps={{
              style: {
                color: isMyMessage ? "#fff" : theme.palette.text.primary,
              },
            }}
          />
        )}
        {!!message.attachments?.length && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              minWidth: 0,
              mt: message.text ? "8px" : 0,
              mb: "4px",
            }}
          >
            {message.attachments.map((attachment) => (
              <MessageAttachmentChip
                key={attachment.id}
                attachment={attachment}
                compact={compact}
                variant="card"
                onDownload={
                  attachment.status === "ready"
                    ? () => downloadAttachment(attachment)
                    : undefined
                }
              />
            ))}
          </Box>
        )}
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap="4px"
        >
          <Typography
            fontSize="14px"
            textAlign="end"
            color={isMyMessage ? "#FFFFFFB3" : theme.palette.text.secondary}
          >
            {format(
              parseISO(message.sentAt),
              dateTimeFormatLong.format,
              dateTimeFormatLong.locale,
            )}
          </Typography>
          {showReceipt &&
            (readByRecipient ? (
              <DoneAll
                titleAccess={t("messageRead")}
                sx={{ fontSize: 16, color: "#fff" }}
              />
            ) : (
              <Done
                titleAccess={t("messageSent")}
                sx={{ fontSize: 16, color: "#FFFFFFB3" }}
              />
            ))}
        </Box>
      </Box>
    </Box>
  );
};

export default Message;
